import type {
  AuthUser,
  Candidate,
  CandidateNote,
  Criterion,
  EmploymentType,
  UserProfile,
  HiringMatrix,
  Industry,
  Interview,
  InterviewOutcome,
  InterviewStatus,
  InterviewType,
  LocationType,
  MatrixFilter,
  OrgCustomRole,
  Organization,
  Permission,
  Position,
  RoleInfo,
  Seniority,
  User,
} from "@/types";
import { getStoredToken, isTokenExpired, clearStoredToken } from "@/types";

export interface ExistingCandidate {
  id: number;
  name: string | null;
  filename: string | null;
}

export class DuplicateError extends Error {
  existingCandidate: ExistingCandidate;
  constructor(message: string, existing: ExistingCandidate) {
    super(message);
    this.name = "DuplicateError";
    this.existingCandidate = existing;
  }
}

// Global callback for auth expiry — set by App.tsx
let onAuthExpired: (() => void) | null = null;
export function setOnAuthExpired(cb: () => void) {
  onAuthExpired = cb;
}

function authHeaders(): Record<string, string> {
  const token = getStoredToken();
  if (!token) return {};
  if (isTokenExpired(token)) {
    clearStoredToken();
    onAuthExpired?.();
    return {};
  }
  return { authorization: `Bearer ${token}` };
}

async function handle<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    clearStoredToken();
    onAuthExpired?.();
    throw new Error("Session expired. Please log in again.");
  }
  if (!res.ok) {
    const text = await res.text();
    let errJson: Record<string, unknown> = {};
    try { errJson = JSON.parse(text) as Record<string, unknown>; } catch { /* not JSON */ }
    const detail = typeof errJson?.error === "string" ? errJson.error : (text || res.statusText);
    if (res.status === 409 && errJson?.existingCandidate) {
      throw new DuplicateError(detail, errJson.existingCandidate as ExistingCandidate);
    }
    throw new Error(`${res.status} ${res.statusText}: ${detail}`);
  }
  return (await res.json()) as T;
}

function get<T>(url: string): Promise<T> {
  return fetch(url, { headers: authHeaders() }).then((r) => handle<T>(r));
}

function post<T>(url: string, body: unknown): Promise<T> {
  return fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  }).then((r) => handle<T>(r));
}

function patch<T>(url: string, body: unknown): Promise<T> {
  return fetch(url, {
    method: "PATCH",
    headers: { "content-type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  }).then((r) => handle<T>(r));
}

function put<T>(url: string, body: unknown): Promise<T> {
  return fetch(url, {
    method: "PUT",
    headers: { "content-type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  }).then((r) => handle<T>(r));
}

function del<T>(url: string): Promise<T> {
  return fetch(url, { method: "DELETE", headers: authHeaders() }).then((r) => handle<T>(r));
}

export const api = {
  // --- Auth ---
  login: (username: string, password: string) =>
    fetch("/api/auth/login", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ username, password }),
    }).then((r) => handle<{ token: string; user: AuthUser }>(r)),

  signup: (body: { display_name: string; email: string; password: string }) =>
    fetch("/api/auth/signup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => handle<{ token: string; user: AuthUser }>(r)),

  googleAuth: (code: string, redirectUri: string) =>
    fetch("/api/auth/google", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ code, redirect_uri: redirectUri }),
    }).then((r) => handle<{ token: string; user: AuthUser }>(r)),

  logout: () => post<{ ok: true }>("/api/auth/logout", {}),

  me: () => get<{ user: AuthUser }>("/api/auth/me"),

  onboard: (name: string, logo?: string | null, banner?: string | null) =>
    post<{ token: string; user: AuthUser }>("/api/onboard", { name, logo, banner }),

  getProfile: () => get<{ profile: UserProfile }>("/api/auth/profile"),

  updateProfile: (body: { display_name?: string; email?: string; avatar?: string; password?: string }) =>
    patch<{ profile: UserProfile }>("/api/auth/profile", body),

  // --- Roles ---
  listRoles: () => get<{ roles: RoleInfo[] }>("/api/roles"),
  updateRolePermissions: (name: string, permissions: string[]) =>
    put<{ ok: boolean }>(`/api/roles/${name}`, { permissions }),

  // --- Permissions catalog ---
  listPermissions: () => get<{ permissions: Permission[] }>("/api/permissions"),
  createPermission: (body: { value: string; label: string; resource: string; action: string; access_level: string }) =>
    post<{ permission: Permission }>("/api/permissions", body),
  togglePermission: (id: number, enabled: boolean) =>
    patch<{ ok: true }>(`/api/permissions/${id}`, { enabled: enabled ? 1 : 0 }),
  deletePermission: (id: number) => del<{ ok: true }>(`/api/permissions/${id}`),

  // --- Org Custom Roles ---
  listOrgRoles: (orgId: number) =>
    get<{ system_roles: RoleInfo[]; custom_roles: OrgCustomRole[] }>(`/api/organizations/${orgId}/roles`),
  createOrgRole: (orgId: number, body: { name: string; label: string; permissions: string[] }) =>
    post<{ role: OrgCustomRole }>(`/api/organizations/${orgId}/roles`, body),
  updateOrgRole: (orgId: number, roleId: number, body: { label?: string; permissions?: string[] }) =>
    put<{ ok: boolean }>(`/api/organizations/${orgId}/roles/${roleId}`, body),
  deleteOrgRole: (orgId: number, roleId: number) =>
    del<{ ok: true }>(`/api/organizations/${orgId}/roles/${roleId}`),

  // --- Users ---
  listUsers: () => get<{ users: User[] }>("/api/users"),

  createUser: (body: { username: string; password: string; role: string }) =>
    post<{ user: User }>("/api/users", body),

  updateUser: (id: number, body: { username?: string; role?: string; password?: string; org_id?: number | null }) =>
    patch<{ ok: true }>(`/api/users/${id}`, body),

  deleteUser: (id: number) => del<{ ok: true }>(`/api/users/${id}`),

  // --- Positions ---
  listPositions: () => get<{ positions: Position[] }>("/api/positions"),

  createPosition: (body: {
    title: string;
    description?: string;
    seniority?: Seniority;
    industry?: Industry;
    employment_type?: EmploymentType;
    location?: LocationType;
  }) => post<{ position: Position }>("/api/positions", body),

  deletePosition: (id: number) => del<{ ok: true }>(`/api/positions/${id}`),

  // --- Hiring Matrix ---
  listMatrices: (positionId?: number) =>
    get<{ matrices: HiringMatrix[] }>(
      positionId ? `/api/matrices?position_id=${positionId}` : "/api/matrices"
    ),

  createMatrix: (body: { position_id: number; name: string; employment_type?: string; criteria: Criterion[]; filters?: MatrixFilter[] }) =>
    post<{ matrix: HiringMatrix }>("/api/matrices", body),

  updateMatrix: (id: number, body: { name: string; employment_type?: string; criteria: Criterion[]; filters?: MatrixFilter[] }) =>
    put<{ ok: true; rescreened_count: number }>(`/api/matrices/${id}`, body),

  deleteMatrix: (id: number) => del<{ ok: true }>(`/api/matrices/${id}`),

  // --- Candidates ---
  listCandidates: (opts?: { positionId?: number; matrixId?: number; archived?: boolean }) => {
    const params = new URLSearchParams();
    if (opts?.matrixId) params.set("matrix_id", String(opts.matrixId));
    else if (opts?.positionId) params.set("position_id", String(opts.positionId));
    if (opts?.archived) params.set("archived", "true");
    const qs = params.toString();
    return get<{ candidates: Candidate[] }>(`/api/candidates${qs ? `?${qs}` : ""}`);
  },

  archiveCandidate: (id: number, archived: boolean) =>
    patch<{ ok: true; rescreening?: boolean }>(`/api/candidates/${id}`, { archived }),

  assignCandidateOrg: (id: number, org_id: number | null) =>
    patch<{ ok: true }>(`/api/candidates/${id}`, { org_id }),

  updateCandidateResume: (
    id: number,
    body: {
      resume_text: string;
      filename?: string;
      resume_file_base64?: string | null;
      resume_file_mime?: string | null;
    }
  ) => patch<{ ok: true; rescreening: boolean }>(`/api/candidates/${id}`, body),

  addCandidateInfo: (id: number, additional_info: string) =>
    patch<{ ok: true; rescreening: boolean }>(`/api/candidates/${id}`, { additional_info }),

  deleteCandidate: (id: number) => del<{ ok: true }>(`/api/candidates/${id}`),

  dedupCandidates: () =>
    post<{ deleted: number; detail: { hash_pass: number; name_pass: number; email_pass: number } }>(
      "/api/candidates/dedup",
      {}
    ),

  getCandidateProfile: (candidateId: number) =>
    get<{ candidate: Candidate; profile: unknown; documents: unknown[]; emergency_contacts: unknown[]; dependents: unknown[]; salary: unknown }>(
      `/api/candidates/profile?candidate_id=${candidateId}`
    ),

  updateCandidateProfile: (body: Record<string, unknown> & { candidate_id: number; name?: string }) =>
    patch<{ ok: true }>("/api/candidates/profile", body),

  extractCandidateEduWork: (candidateId: number) =>
    post<{
      education_history: Array<{ school: string; degree: string; year: string }>;
      work_experience: Array<{ company: string; title: string; from: string; to: string; description: string }>;
    }>("/api/candidates/extract-eduwork", { candidate_id: candidateId }),

  fetchResumeFile: async (candidateId: number): Promise<{ blob: Blob; mime: string }> => {
    const res = await fetch(`/api/candidates/resume-file?candidate_id=${candidateId}`, {
      headers: authHeaders(),
    });
    if (!res.ok) {
      let detail = "";
      try {
        const err = (await res.json()) as { error?: string };
        detail = err.error || "";
      } catch { /* ignore */ }
      throw new Error(detail || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    return { blob, mime: res.headers.get("content-type") || blob.type || "application/octet-stream" };
  },

  // --- Screen ---
  screen: (body: {
    position_id: number;
    matrix_id: number;
    filename: string;
    resume_text: string;
    photo?: string | null;
    resume_file_base64?: string | null;
    resume_file_mime?: string | null;
    force?: boolean;
  }) => post<{ candidate: Candidate }>("/api/screen", body),

  // --- Pipeline (full multi-agent) ---
  runPipeline: (body: {
    position_id: number;
    matrix_id: number;
    filename: string;
    resume_text: string;
    photo?: string | null;
    resume_file_base64?: string | null;
    resume_file_mime?: string | null;
  }) => post<{
    candidate: Candidate;
    stage: string;
    salary_benchmark: unknown;
    intake: unknown;
    pipeline_events: Array<{ type: string; timestamp: string }>;
  }>("/api/pipeline/run", body),

  // --- Agents ---
  runIntakeAgent: (body: { candidate_id: number; resume_text: string }) =>
    post<{ intake: unknown; candidate_id: number }>("/api/agents/intake", body),

  runSalaryAgent: (body: { candidate_id: number; position_id: number }) =>
    post<{ salary: unknown; candidate_id: number }>("/api/agents/salary", body),

  runLifecycleAgent: (body: { candidate_id: number; action: string; stage?: string; notes?: string }) =>
    post<{ candidate_id: number; current_stage: string; previous_stage: string; history: unknown[] }>(
      "/api/agents/lifecycle", body
    ),

  // --- Candidate sub-resources ---
  getCandidateStages: (candidateId: number) =>
    get<{ stages: unknown[] }>(`/api/candidates/stages?candidate_id=${candidateId}`),

  getCandidateSalary: (candidateId: number) =>
    get<{ salary: unknown }>(`/api/candidates/salary?candidate_id=${candidateId}`),

  // --- Events ---
  listEvents: (opts?: { entityType?: string; entityId?: number; eventType?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (opts?.entityType) params.set("entity_type", opts.entityType);
    if (opts?.entityId) params.set("entity_id", String(opts.entityId));
    if (opts?.eventType) params.set("event_type", opts.eventType);
    if (opts?.limit) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return get<{ events: unknown[] }>(`/api/events${qs ? `?${qs}` : ""}`);
  },

  // --- Notifications ---
  listNotifications: (unread?: boolean) =>
    get<{ notifications: unknown[]; unread_count: number }>(
      `/api/notifications${unread ? "?unread=true" : ""}`
    ),

  markNotificationRead: (id: number) =>
    patch<{ ok: true }>("/api/notifications", { id }),

  markAllNotificationsRead: () =>
    patch<{ ok: true }>("/api/notifications", { mark_all_read: true }),

  // --- Image uploads ---
  uploadImage: (body: { imageData: string; mime: string; slot: string }) =>
    post<{ url: string }>("/api/images", body),

  // --- Organizations ---
  listOrganizations: () =>
    get<{ organizations: Organization[] }>("/api/organizations"),

  createOrganization: (body: { name: string; slug: string; logo?: string }) =>
    post<{ organization: Organization }>("/api/organizations", body),

  updateOrganization: (id: number, body: { name?: string; slug?: string; logo?: string | null; banner?: string | null }) =>
    patch<{ ok: true }>(`/api/organizations/${id}`, body),

  deleteOrganization: (id: number) =>
    del<{ ok: true }>(`/api/organizations/${id}`),

  getOrgAnalytics: (orgId: number, period: string, startDate: string) =>
    get<{
      org: { id: number; name: string };
      period: string;
      screening_over_time: { label: string; count: number }[];
      summary: { total_screened: number; strong_hire_count: number; consider_count: number; reject_count: number; avg_match_score: number | null };
      time_to_hire: { avg_days: number | null; min_days: number | null; max_days: number | null; count: number };
      failure_reasons: { criterion: string; fail_count: number; avg_score: number }[];
    }>(`/api/organizations/${orgId}/analytics?period=${period}&start_date=${startDate}`),

  // --- Users (with org_id) ---
  createUserInOrg: (body: { username: string; password: string; role: string; org_id: number | null }) =>
    post<{ user: User }>("/api/users", body),

  // --- Notes ---
  listNotes: (candidateId: number) =>
    get<{ notes: CandidateNote[] }>(`/api/candidates/notes?candidate_id=${candidateId}`),
  createNote: (body: { candidate_id: number; content: string }) =>
    post<{ note: CandidateNote }>("/api/candidates/notes", body),
  updateNote: (id: number, content: string) =>
    patch<{ ok: true }>("/api/candidates/notes", { id, content }),
  deleteNote: (id: number) =>
    del<{ ok: true }>(`/api/candidates/notes?id=${id}`),

  // --- Shared PDFs ---
  createShare: (endorsement_input: string) =>
    post<{ id: string }>("/api/shares", { endorsement_input }),

  getShare: (id: string) =>
    get<{
      endorsement_input: string;
      comments: Array<{ id: number; commenter_name: string; body: string; created_at: number; page_number: number; parent_id: number | null }>;
    }>(`/api/shares/${id}`),

  addShareComment: (id: string, commenter_name: string, body: string, page_number: number, parent_id?: number | null) =>
    post<{ comment: { id: number; commenter_name: string; body: string; created_at: number; page_number: number; parent_id: number | null } }>(
      `/api/shares/${id}`,
      { commenter_name, body, page_number, parent_id }
    ),

  // --- Interviews ---
  listInterviews: (candidateId?: number) =>
    get<{ interviews: Interview[] }>(
      candidateId ? `/api/interviews?candidate_id=${candidateId}` : "/api/interviews"
    ),
  createInterview: (body: {
    candidate_id: number;
    scheduled_at: string;
    duration_minutes?: number;
    interview_type: InterviewType;
    location?: string;
    interviewer_id?: number;
    interviewer_name?: string;
    notes?: string;
  }) => post<{ interview: Interview }>("/api/interviews", body),
  updateInterview: (id: number, body: Partial<{
    scheduled_at: string;
    duration_minutes: number;
    interview_type: InterviewType;
    status: InterviewStatus;
    outcome: InterviewOutcome;
    location: string;
    interviewer_id: number | null;
    interviewer_name: string;
    notes: string;
  }>) => patch<{ ok: true }>("/api/interviews", { id, ...body }),
  deleteInterview: (id: number) =>
    del<{ ok: true }>(`/api/interviews?id=${id}`),

  // --- Billing ---
  getBillingUsage: (orgId?: number) =>
    get<{
      org_id: number | null;
      month: string;
      total_tokens: number;
      credits_used: number;
      monthly_limit: number;
      overage_credits: number;
      overage_cost_usd: number;
      is_over_limit: boolean;
      by_function: Array<{ function_name: string; total_tokens: number; call_count: number; credits: number }>;
    }>(`/api/billing/usage${orgId != null ? `?org_id=${orgId}` : ""}`),
};
